import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Award, CalendarPlus, CalendarX2, History, Loader2, ShieldAlert, UserX } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useUsers } from "@/hooks/useUsers";
import { useRemoveExam, useResetProgress, useStudentData } from "@/hooks/useStudent";
import { ScheduleExamDialog } from "@/components/student/ScheduleExamDialog";
import { ProfileHeader } from "@/components/student/ProfileHeader";
import { StudentInfo } from "@/components/student/StudentInfo";
import { ProgressSummary } from "@/components/student/ProgressSummary";
import { SectionPanel } from "@/components/student/SectionPanel";
import { ModuleList } from "@/components/student/ModuleList";
import { ExamList } from "@/components/student/ExamList";
import { SessionList } from "@/components/student/SessionList";
import { LastModuleStats } from "@/components/student/LastModuleStats";
import { UserConfirm } from "@/components/users/UserConfirm";
import { ConfirmDialog } from "@/components/users/ConfirmDialog";
import { L } from "@/i18n";

const Back = () => (
  <Link to="/users" className="inline-flex items-center gap-2 mb-6 text-xs text-slate-400 hover:text-amber-400 transition-colors" data-testid="student-back-link">
    <ArrowLeft className="h-3.5 w-3.5" /> {L("Back to users", "Voltar a utilizadores")}
  </Link>
);

export default function StudentProfile() {
  const { id } = useParams();
  const { user: me } = useAuth();
  const { data: users, isLoading } = useUsers();
  const q = useStudentData(id);
  const reset = useResetProgress();
  const [request, setRequest] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [examToRemove, setExamToRemove] = useState(null);
  const removeExam = useRemoveExam();
  const user = users?.find((u) => u.id === id);

  if (isLoading) return <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  if (!user) {
    return (
      <div data-testid="student-not-found">
        <Back />
        <div className="rounded-lg border border-dashed border-slate-700 p-12 text-center">
          <UserX className="mx-auto h-8 w-8 text-slate-500" />
          <p className="mt-3 text-sm text-slate-300">{L("User not found.", "Utilizador não encontrado.")}</p>
        </div>
      </div>
    );
  }

  const confirmRemove = () =>
    removeExam.mutate(
      { userId: id, username: user.username, examId: examToRemove.id, module: examToRemove.module },
      { onSettled: () => setExamToRemove(null) },
    );

  const confirmReset = () => reset.mutate({ id, username: user.username }, { onSettled: () => setResetOpen(false) });

  return (
    <div data-testid="student-profile-page">
      <Back />
      <ProfileHeader user={user} isSelf={user.id === me?.id} onRequest={setRequest} onReset={() => setResetOpen(true)} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 grid gap-6 content-start">
          <ProgressSummary modules={q.modules.data} concluded={q.concluded.data} failed={q.failed.data} failedCount={q.failedCount.data} />
          <LastModuleStats user={user} sessionsQuery={q.sessions} />
          <SectionPanel
            title={L("Recent quizzes & exams", "Últimos quizzes e exames")}
            subtitle={L("Last 20 sessions done in the app", "Últimas 20 sessões feitas na app")}
            query={q.sessions}
            emptyIcon={History}
            emptyText={L("No sessions done yet.", "Ainda sem sessões feitas.")}
            errorText={L("Session history is not provided by the API yet.", "O histórico de sessões ainda não é fornecido pela API.")}
            testId="student-sessions"
          >
            {(items) => <SessionList sessions={items} />}
          </SectionPanel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SectionPanel title={L("Completed modules", "Módulos concluídos")} query={q.concluded} emptyIcon={Award} emptyText={L("No completed modules.", "Sem módulos concluídos.")} testId="student-concluded">
              {(items) => <ModuleList items={items} prefix="concluded" tone="bg-emerald-500/10 text-emerald-400" />}
            </SectionPanel>
            <SectionPanel title={L("Failures / failed modules", "Falhas / reprovações")} query={q.failed} emptyIcon={ShieldAlert} emptyText={L("No failed modules recorded.", "Sem reprovações registadas.")} testId="student-failed">
              {(items) => <ModuleList items={items} prefix="failed" tone="bg-rose-500/10 text-rose-400" />}
            </SectionPanel>
          </div>
          <SectionPanel title={L("Scheduled exams", "Exames agendados")} subtitle={L("Exams assigned to the student", "Exames atribuídos ao aluno")} query={q.exams} emptyIcon={CalendarX2} emptyText={L("No exams assigned.", "Sem exames atribuídos.")} testId="student-exams"
              action={
                <button onClick={() => setScheduleOpen(true)} className="flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-[#0a1425] hover:bg-amber-400 transition-colors" data-testid="student-schedule-exam-button">
                  <CalendarPlus className="h-3.5 w-3.5" /> {L("Schedule exam", "Agendar exame")}
                </button>
              }
            >
            {(items) => <ExamList exams={items} onRemove={setExamToRemove} />}
          </SectionPanel>
        </div>
        <div className="lg:col-span-4"><StudentInfo user={user} /></div>
      </div>

      <UserConfirm request={request} onClose={() => setRequest(null)} />
      <ScheduleExamDialog open={scheduleOpen} onOpenChange={setScheduleOpen} user={user} />
      <ConfirmDialog
        open={!!examToRemove}
        onOpenChange={(o) => !o && setExamToRemove(null)}
        title={L("Remove exam from student?", "Remover exame do aluno?")}
        description={examToRemove ? <><b className="text-slate-200">{examToRemove.module} · {examToRemove.module_name}</b> ({examToRemove.exam_date}) {L("will no longer be assigned to {u}.", "deixa de estar atribuído a {u}.", { u: user.username })}</> : ""}
        confirmLabel={L("Remove", "Remover")}
        danger
        loading={removeExam.isPending}
        onConfirm={confirmRemove}
      />
      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title={L("Reset student progress?", "Repor progresso do aluno?")}
        description={<><b className="text-slate-200">{user.username}</b>{L(": this student's quiz progress will be deleted. This action cannot be undone.", ": o progresso de quizzes deste aluno será apagado. Esta ação não pode ser desfeita.")}</>}
        confirmLabel={L("Reset progress", "Repor progresso")}
        danger
        loading={reset.isPending}
        onConfirm={confirmReset}
      />
    </div>
  );
}
